// src/components/partner/ManageCohortStaffModal.jsx
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  RefreshCw,
  Save,
  Search,
  Users,
  X,
} from "lucide-react";
import {
  fetchPartnerMemberCohortResponsibilities,
  setPartnerMemberCohortResponsibilities,
} from "../../lib/partner/partnerTeam";

function statusClasses(status) {
  switch (status) {
    case "active":
      return "bg-green-100 text-green-800";

    case "completed":
      return "bg-blue-100 text-blue-800";

    case "archived":
      return "bg-gray-200 text-gray-700";

    default:
      return "bg-amber-100 text-amber-800";
  }
}

function normaliseRow(row) {
  return {
    ...row,
    is_programme_manager: Boolean(
      row?.is_programme_manager
    ),
    is_facilitator: Boolean(
      row?.is_facilitator
    ),
  };
}

export default function ManageCohortStaffModal({
  open,
  member,
  initialCohortId = "",
  onClose,
  onUpdated,
}) {
  const [assignments, setAssignments] =
    useState([]);
  const [draftByCohortId, setDraftByCohortId] =
    useState({});
  const [search, setSearch] =
    useState("");
  const [loading, setLoading] =
    useState(false);
  const [saving, setSaving] =
    useState(false);
  const [error, setError] =
    useState("");

  const loadAssignments =
    useCallback(async () => {
      if (
        !member
          ?.partner_member_id
      ) {
        return;
      }

      try {
        setLoading(true);
        setError("");

        const rows =
          await fetchPartnerMemberCohortResponsibilities(
            member.partner_member_id
          );

        const normalisedRows =
          rows.map(normaliseRow);

        setAssignments(
          normalisedRows
        );

        const nextDraft = {};

        normalisedRows.forEach(
          (row) => {
            nextDraft[
              row.cohort_id
            ] = {
              isProgrammeManager:
                row.is_programme_manager,
              isFacilitator:
                row.is_facilitator,
            };
          }
        );

        setDraftByCohortId(
          nextDraft
        );
      } catch (loadError) {
        console.error(
          "Loading cohort staff responsibilities failed:",
          loadError
        );

        setError(
          loadError?.message ||
            "The cohort staff responsibilities could not be loaded."
        );
      } finally {
        setLoading(false);
      }
    }, [member]);

  useEffect(() => {
    if (!open || !member) {
      setAssignments([]);
      setDraftByCohortId({});
      setSearch("");
      setError("");
      setLoading(false);
      setSaving(false);

      return undefined;
    }

    loadAssignments();

    function handleEscape(event) {
      if (
        event.key === "Escape" &&
        !saving
      ) {
        onClose?.();
      }
    }

    window.addEventListener(
      "keydown",
      handleEscape
    );

    return () => {
      window.removeEventListener(
        "keydown",
        handleEscape
      );
    };
  }, [
    open,
    member,
    saving,
    onClose,
    loadAssignments,
  ]);

  const filteredAssignments =
    useMemo(() => {
      const cleanSearch =
        search
          .trim()
          .toLowerCase();

      const rows =
        initialCohortId
          ? assignments.filter(
              (assignment) =>
                String(
                  assignment.cohort_id
                ) ===
                String(
                  initialCohortId
                )
            )
          : assignments;

      if (!cleanSearch) {
        return rows;
      }

      return rows.filter(
        (assignment) =>
          String(
            assignment.cohort_name ||
              ""
          )
            .toLowerCase()
            .includes(cleanSearch)
      );
    }, [
      assignments,
      search,
      initialCohortId,
    ]);

  if (!open || !member) {
    return null;
  }

  function handleBackdropClick(
    event
  ) {
    if (
      event.target ===
        event.currentTarget &&
      !saving
    ) {
      onClose?.();
    }
  }

  function toggleResponsibility(
    cohortId,
    field
  ) {
    setDraftByCohortId(
      (current) => {
        const existing =
          current[cohortId] || {
            isProgrammeManager:
              false,
            isFacilitator:
              false,
          };

        return {
          ...current,
          [cohortId]: {
            ...existing,
            [field]:
              !existing[field],
          },
        };
      }
    );
  }

  async function handleSave() {
    try {
      setSaving(true);
      setError("");

      const changedRows =
        assignments.filter(
          (assignment) => {
            if (
              assignment.cohort_status ===
              "archived"
            ) {
              return false;
            }

            const draft =
              draftByCohortId[
                assignment.cohort_id
              ] || {
                isProgrammeManager:
                  false,
                isFacilitator:
                  false,
              };

            return (
              draft.isProgrammeManager !==
                Boolean(
                  assignment.is_programme_manager
                ) ||
              draft.isFacilitator !==
                Boolean(
                  assignment.is_facilitator
                )
            );
          }
        );

      await Promise.all(
        changedRows.map(
          (assignment) => {
            const draft =
              draftByCohortId[
                assignment.cohort_id
              ] || {
                isProgrammeManager:
                  false,
                isFacilitator:
                  false,
              };

            return setPartnerMemberCohortResponsibilities(
              {
                partnerMemberId:
                  member.partner_member_id,
                cohortId:
                  assignment.cohort_id,
                isProgrammeManager:
                  draft.isProgrammeManager,
                isFacilitator:
                  draft.isFacilitator,
              }
            );
          }
        )
      );

      await onUpdated?.({
        member,
      });

      onClose?.();
    } catch (saveError) {
      console.error(
        "Saving cohort staff responsibilities failed:",
        saveError
      );

      setError(
        saveError?.message ||
          "The cohort staff responsibilities could not be saved."
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      role="presentation"
      onMouseDown={
        handleBackdropClick
      }
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/45 p-0 sm:items-center sm:p-6"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="manage-cohort-staff-title"
        className="flex max-h-[95vh] w-full flex-col overflow-hidden rounded-t-[28px] bg-white shadow-2xl sm:max-w-2xl sm:rounded-[28px]"
      >
        <header className="flex items-start justify-between border-b border-[#EFEFEF] px-6 py-5">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.14em] text-[#FF914D]">
              Cohort responsibilities
            </p>

            <h2
              id="manage-cohort-staff-title"
              className="mt-1 text-2xl font-black text-[#333333]"
            >
              Manage staff role
            </h2>

            <p className="mt-2 text-sm text-[#757575]">
              {member.full_name ||
                member.email ||
                "Team member"}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label="Close cohort staff form"
            className="flex h-10 w-10 items-center justify-center rounded-full text-[#757575] transition hover:bg-[#FFF4EC] hover:text-[#FF914D] disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        {!initialCohortId && (
          <div className="border-b border-[#EFEFEF] px-6 py-4">
            <div className="relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#999999]" />

              <input
                value={search}
                onChange={(event) =>
                  setSearch(
                    event.target.value
                  )
                }
                placeholder="Search cohorts"
                className="w-full rounded-2xl border border-[#DDDDDD] py-3 pl-11 pr-4 text-sm outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
              />
            </div>
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto p-6">
          {error && (
            <div
              role="alert"
              className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"
            >
              {error}
            </div>
          )}

          {loading ? (
            <div className="py-10 text-center text-sm font-semibold text-[#757575]">
              Loading cohort responsibilities…
            </div>
          ) : filteredAssignments.length ===
            0 ? (
            <div className="rounded-2xl border border-dashed border-[#DDDDDD] p-8 text-center">
              <Users className="mx-auto h-7 w-7 text-[#FF914D]" />

              <p className="mt-3 font-black">
                No cohorts found
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredAssignments.map(
                (assignment) => {
                  const archived =
                    assignment.cohort_status ===
                    "archived";

                  const draft =
                    draftByCohortId[
                      assignment.cohort_id
                    ] || {
                      isProgrammeManager:
                        false,
                      isFacilitator:
                        false,
                    };

                  return (
                    <article
                      key={
                        assignment.cohort_id
                      }
                      className={[
                        "rounded-2xl border p-4",
                        archived
                          ? "border-[#EFEFEF] bg-[#FAFAFA] opacity-60"
                          : "border-[#EFEFEF] bg-white",
                      ].join(" ")}
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-black text-[#333333]">
                            {
                              assignment.cohort_name
                            }
                          </p>

                          {archived && (
                            <p className="mt-1 text-xs text-[#757575]">
                              Archived cohorts cannot have staff responsibilities changed.
                            </p>
                          )}
                        </div>

                        <span
                          className={[
                            "w-fit rounded-full px-3 py-1 text-xs font-black capitalize",
                            statusClasses(
                              assignment.cohort_status
                            ),
                          ].join(" ")}
                        >
                          {
                            assignment.cohort_status
                          }
                        </span>
                      </div>

                      <div className="mt-4 grid gap-3 sm:grid-cols-2">
                        <label
                          className={[
                            "flex items-center gap-3 rounded-2xl border px-4 py-3",
                            draft.isProgrammeManager
                              ? "border-[#FF914D] bg-[#FFF7ED]"
                              : "border-[#E5E5E5] bg-white",
                            archived || saving
                              ? "cursor-not-allowed"
                              : "cursor-pointer hover:border-[#FF914D]",
                          ].join(" ")}
                        >
                          <input
                            type="checkbox"
                            checked={
                              draft.isProgrammeManager
                            }
                            disabled={
                              archived || saving
                            }
                            onChange={() =>
                              toggleResponsibility(
                                assignment.cohort_id,
                                "isProgrammeManager"
                              )
                            }
                            className="h-5 w-5 accent-[#FF914D]"
                          />

                          <span className="text-sm font-black text-[#333333]">
                            Programme Manager
                          </span>
                        </label>

                        <label
                          className={[
                            "flex items-center gap-3 rounded-2xl border px-4 py-3",
                            draft.isFacilitator
                              ? "border-[#FF914D] bg-[#FFF7ED]"
                              : "border-[#E5E5E5] bg-white",
                            archived || saving
                              ? "cursor-not-allowed"
                              : "cursor-pointer hover:border-[#FF914D]",
                          ].join(" ")}
                        >
                          <input
                            type="checkbox"
                            checked={
                              draft.isFacilitator
                            }
                            disabled={
                              archived || saving
                            }
                            onChange={() =>
                              toggleResponsibility(
                                assignment.cohort_id,
                                "isFacilitator"
                              )
                            }
                            className="h-5 w-5 accent-[#FF914D]"
                          />

                          <span className="text-sm font-black text-[#333333]">
                            Facilitator
                          </span>
                        </label>
                      </div>
                    </article>
                  );
                }
              )}
            </div>
          )}
        </div>

        <footer className="flex flex-col-reverse gap-3 border-t border-[#EFEFEF] px-6 py-5 sm:flex-row sm:items-center sm:justify-end">
          <button
            type="button"
            onClick={
              loadAssignments
            }
            disabled={
              loading || saving
            }
            className="inline-flex items-center justify-center rounded-full border border-[#DDDDDD] px-4 py-2.5 text-sm font-black text-[#555555] disabled:opacity-50"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Reset
          </button>

          <button
            type="button"
            onClick={handleSave}
            disabled={
              loading || saving
            }
            className="inline-flex items-center justify-center rounded-full bg-[#FF914D] px-5 py-2.5 text-sm font-black text-white transition hover:bg-[#E9782F] disabled:opacity-50"
          >
            <Save className="mr-2 h-4 w-4" />

            {saving
              ? "Saving…"
              : "Save responsibilities"}
          </button>
        </footer>
      </div>
    </div>
  );
}
